/* eslint-disable */
import { useState, useCallback, useEffect, useRef, FC } from 'react';
import * as MapGL from 'react-map-gl';
import { zonesLayers } from './layerStyles';
import { Area, Zone, Country } from '@/models/location';
import { Placeholder } from './Placeholder';
import { MAPBOX_TOKEN } from '@/features/map/constants';
import { Ad, useLazyGetZoneAdsQuery } from '@/models/ad';
import { css, cx } from '@emotion/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation } from 'swiper/modules';
import { VideoSlide } from '@/features/map/VideoSlide';
import { generateRandomLatLonArray } from '@/features/map/utils';
import { useGeoJson } from '@/features/map';
import { LatLng } from '@/models/location';

import 'swiper/css';
import 'swiper/css/navigation';
import { PulsingMarker } from '@/features/map/PulsingMarker';

type Props = {
  country?: Country;
  area?: Area;
  zones?: Zone[];
  height?: string;
  width?: string;
};

export const MapWithDevices: FC<Props> = ({
  country,
  area,
  zones,
  height = '400px',
  width = '100%',
}) => {
  const { geojson } = useGeoJson({ zones });
  const [loaded, setLoaded] = useState(false);
  const mapRef = useRef<MapGL.MapRef>(null);
  const [selectedPosition, setSelectedPosition] = useState<
    | {
        latitude: number;
        longitude: number;
      }
    | undefined
  >();
  const [trigger, { data: zoneAds }] = useLazyGetZoneAdsQuery();
  const [rotations, setRotations] = useState<number[]>([]);

  const setLocation = useCallback(
    ({
      longitude,
      latitude,
      zoom,
    }: {
      longitude: number;
      latitude: number;
      zoom: number;
    }) => {
      mapRef.current?.flyTo({
        center: [longitude, latitude],
        duration: 2000,
        zoom,
      });
    },
    []
  );

  const selectZone = (e: MapGL.MapLayerMouseEvent) => {
    const latLng = e.lngLat;
    const feature = e?.features ? e?.features[0] : null;
    const id = feature?.properties?.id as number;
    const zoneIds = zones?.map((it) => it.id) || [];

    if (id && latLng) {
      setSelectedPosition({
        latitude: latLng.lat,
        longitude: latLng.lng,
      });
    } else {
      setSelectedPosition(undefined);
    }

    if (zoneIds?.includes(id)) {
      trigger({ zoneId: id });
    }
  };

  useEffect(() => {
    if (country && area && loaded) {
      const { longitude, latitude } = area;
      setLocation({ longitude, latitude, zoom: 12 });
    }
  }, [area, loaded]);

  // useEffect(() => {
  //   if (country && loaded) {
  //     const { longitude, latitude } = country;
  //     setLocation({ longitude, latitude, zoom: 5 });
  //   }
  // }, [country, loaded]);

  useEffect(() => {
    if (area?.id && lastPositions[area?.id]) {
      const r = lastPositions[area?.id].map(() =>
        Math.floor(Math.random() * (360 - 0 + 1) + 0)
      );
      setRotations(r);
    }
  }, [lastPositions, area?.id]);

  return (
    <div className="relative rounded-md overflow-hidden">
      <Placeholder hidden={loaded} />
      <MapGL.Map
        ref={mapRef}
        style={{ width, height }}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/light-v9"
        onLoad={() => setLoaded(true)}
        scrollZoom={false}
        interactiveLayerIds={['zones-fill']}
        onClick={selectZone}
        initialViewState={{
          longitude: 11.582423085886072,
          latitude: 50.0535131370207,
          zoom: 3,
        }}
      >
        <MapGL.NavigationControl
          style={{
            right: 10,
            top: 10,
          }}
        />
        {selectedPosition && (
          <MapGL.Popup
            anchor="top"
            longitude={selectedPosition.longitude}
            latitude={selectedPosition.latitude}
            onClose={() => setSelectedPosition(undefined)}
            className={css`
              .mapboxgl-popup-tip {
                border-bottom-color: rgb(213, 0, 50);
              }
              .mapboxgl-popup-content {
                padding: 5px 0 0 0;
                border-radius: 10px;
                background-color: rgb(213, 0, 50);
              }
              .mapboxgl-popup-close-button {
                padding: 0 6px;
                background-color: rgb(213, 0, 50);
                outline: none;

                z-index: 10;
                color: white;
                box-shadow: 0;
                border-radius: 50%;
                width: 24px;
                height: 24px;
                top: -6px;
                right: -6px;
                font-size: 1.2rem;
                box-shadow: 0 0 5px 5px rgba(0, 0, 0, 0.1);
              }
            `}
          >
            <div
              className={cx(
                'bg-black text-white rounded-xl',
                css`
                  width: 240px;
                  height: 144px;
                `
              )}
            >
              {zoneAds ? (
                <Swiper
                  slidesPerView={1}
                  autoplay={{
                    delay: 3000,
                  }}
                  modules={[Autoplay, EffectFade, Navigation]}
                  navigation={true}
                  effect="fade"
                  speed={300}
                  className={css`
                    border-radius: 10px;
                    display: flex;
                    align-content: center;

                    .swiper-button-prev,
                    .swiper-button-next {
                      color: white;

                      &::after {
                        font-size: 24px;
                      }
                    }
                  `}
                >
                  {zoneAds?.map((ad: Ad) => {
                    return (
                      <SwiperSlide
                        key={ad.id}
                        className={css`
                          background-color: black;
                        `}
                      >
                        {({ isActive }) => (
                          <div
                            className={css`
                              width: 240px;
                              height: 144px;
                              overflow: hidden;
                              display: flex;
                              align-content: center;
                            `}
                          >
                            {ad.format.includes('video') && (
                              <VideoSlide uri={ad.url} isActive={isActive} />
                            )}
                            {ad.format.includes('image') && (
                              <img
                                src={ad.url}
                                alt="ads"
                                style={{
                                  width: '100%',
                                  backgroundColor: 'black',
                                  objectFit: 'contain',
                                }}
                              />
                            )}
                          </div>
                        )}
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              ) : (
                <div className="flex justify-center items-center h-full w-full">
                  {/* // TODO: translate */}

                  <h2 className="text-lg font-semibold text-center">
                    No ads available for preview
                  </h2>
                </div>
              )}
            </div>
          </MapGL.Popup>
        )}
        {area?.id &&
          lastPositions[area?.id]?.map((it, i) => (
            <PulsingMarker
              longitude={it.longitude}
              latitude={it.latitude}
              rotation={rotations[i]}
              key={`${it.longitude}-${it.latitude}`}
              index={i}
            />
          ))}
        {geojson && zones && zones.length > 0 && (
          <MapGL.Source id="zones" type="geojson" data={geojson}>
            <MapGL.Layer
              id="zones-fill"
              type="fill"
              source="zones"
              paint={zonesLayers.fill.default}
            />
            <MapGL.Layer
              id="zones-outline"
              type="line"
              source="zones"
              paint={zonesLayers.outline.default}
            />
          </MapGL.Source>
        )}
      </MapGL.Map>
    </div>
  );
};

const krakowCenter = generateRandomLatLonArray(
  50.071442,
  19.922262,
  50.046375,
  19.967594,
  60,
  70
);
const krakowAirport = generateRandomLatLonArray(
  50.076805,
  19.797879,
  50.072279,
  19.816866,
  3,
  8
);
const krakowFull = generateRandomLatLonArray(
  50.085508,
  19.875725,
  50.009915,
  20.034128,
  30,
  40
);

const warsawCenter = generateRandomLatLonArray(
  52.255931,
  20.968572,
  52.205074,
  21.080953,
  80,
  100
);
const warsawAirport1 = generateRandomLatLonArray(
  52.173641,
  20.970891,
  52.163287,
  20.986934,
  10,
  20
);
const warsawAirport2 = generateRandomLatLonArray(
  52.446042,
  20.647602,
  52.441967,
  20.659293,
  5,
  10
);
const warsawFull = generateRandomLatLonArray(
  52.296187,
  20.79986,
  52.101843,
  21.293973,
  40,
  50
);

const wroclawAirport = generateRandomLatLonArray(
  51.11628408562689,
  16.881103512008014,
  51.107071768109044,
  16.900683398613324,
  10,
  20
);

const wroclawCenter = generateRandomLatLonArray(
  51.11689517453972,
  17.024274300244283,
  51.101290361894925,
  17.050825205555725,
  20,
  40
);

const wroclawFull = generateRandomLatLonArray(
  51.152161793121586,
  16.945266435047333,
  51.09248678785946,
  17.095546935540153,
  30,
  60
);

const gdanskCenter = generateRandomLatLonArray(
  54.394242,
  18.601885,
  54.331619,
  18.662695,
  15,
  30
);

const gdanskAirport = generateRandomLatLonArray(
  54.373671,
  18.46009,
  54.360469,
  18.481153,
  5,
  10
);

const sopotCenter = generateRandomLatLonArray(
  54.447991,
  18.559151,
  54.439206,
  18.575507,
  3,
  10
);

const gdyniaCenter = generateRandomLatLonArray(
  54.523116,
  18.531286,
  54.510215,
  18.551497,
  5,
  10
);

const poznanCenter = generateRandomLatLonArray(
  52.429404,
  16.89378,
  52.382035,
  16.974159,
  15,
  30
);

const poznanAirport = generateRandomLatLonArray(
  52.414783,
  16.820544,
  52.410682,
  16.842205,
  3,
  7
);

const lastPositions: { [key: number]: LatLng[] } = {
  1: [...krakowCenter, ...krakowAirport, ...krakowFull],
  3: [...warsawCenter, ...warsawAirport1, ...warsawAirport2, ...warsawFull],
  5: [...wroclawCenter, ...wroclawAirport, ...wroclawFull],
  6: [...gdanskCenter, ...gdanskAirport, ...sopotCenter, ...gdyniaCenter],
  10: [...poznanCenter, ...poznanAirport],
};
